import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { C, F } from '../constants';
import { useTheme } from '../contexts/ThemeContext';

// Did You Know facts about deck intelligence and fundraising
const didYouKnowFacts = [
  "Did you know? Investors spend an average of only 3 minutes 44 seconds reviewing a pitch deck.",
  "Did you know? Decks with clear problem-solution statements have 2.5x higher engagement.",
  "Did you know? The ideal pitch deck length is 10-15 slides for investor presentations.",
  "Did you know? Visual decks with less text per slide get 38% more follow-up meetings.",
  "Did you know? Founders who practice their pitch 40+ times raise 2x more capital.",
  "Did you know? Data-backed claims in decks increase investor trust by 67%.",
  "Did you know? A compelling story arc increases deck memorability by 22x.",
  "Did you know? The best pitch decks are designed for a 3-minute read.",
  "Did you know? 74% of investors reject decks before the 5th slide.",
  "Did you know? Personalized investor decks have 3x higher response rates.",
  "Did you know? Market sizing slides are viewed longest by investors.",
  "Did you know? Decks with team photos get 40% more engagement.",
  "Did you know? A/B testing your deck title can improve open rates by 45%.",
  "Did you know? Investors prefer reading decks on mobile 60% of the time.",
  "Did you know? DecX analyzes 47 data points to score your deck."
];

// DecX Logo Component using actual SVG files
const DecXLogo = ({ animate = true, size = 60, iconOnly = false }) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  
  // Use icon-only logo or full logo based on prop
  const logoSrc = iconOnly 
    ? (isDark ? '/logo-icon-dark.svg' : '/logo-icon-light.svg')
    : (isDark ? '/logo-full-dark.svg' : '/logo-full-light.svg');
  
  return (
    <motion.div
      style={{ position: 'relative' }}
      animate={animate ? {
        scale: [1, 1.02, 1],
      } : {}}
      transition={{
        duration: 2,
        repeat: Infinity,
        ease: "easeInOut"
      }}
    >
      {/* Outer glow */}
      {animate && (
        <motion.div
          style={{
            position: 'absolute',
            inset: -12,
            borderRadius: '50%',
            background: `radial-gradient(circle, ${C.secondary}40 0%, transparent 70%)`,
            filter: 'blur(15px)',
            opacity: 0.6
          }}
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.4, 0.6, 0.4]
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
      )}
      
      {/* Logo Image */}
      <motion.img
        src={logoSrc}
        alt="DecX AI"
        style={{
          width: iconOnly ? size : size * 2.5,
          height: size,
          objectFit: 'contain',
          position: 'relative',
          zIndex: 1
        }}
      />
    </motion.div>
  );
};

// Export the logo component for use elsewhere
export { DecXLogo };

// Main branded loader component
export const BrandedLoader = ({ 
  text = "Loading...", 
  showFacts = true,
  minDuration = 0,
  size = "medium",
  fullScreen = false 
}) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [currentFactIndex, setCurrentFactIndex] = useState(
    Math.floor(Math.random() * didYouKnowFacts.length)
  );
  
  // Rotate facts every 4 seconds
  useEffect(() => {
    if (!showFacts) return;
    
    const interval = setInterval(() => {
      setCurrentFactIndex((prev) => (prev + 1) % didYouKnowFacts.length);
    }, 4000);
    
    return () => clearInterval(interval);
  }, [showFacts]);
  
  const logoSize = size === 'small' ? 48 : size === 'large' ? 80 : 64;
  
  const content = (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '24px',
        padding: '40px',
        textAlign: 'center'
      }}
    >
      {/* Logo */}
      <DecXLogo animate={true} size={logoSize} />
      
      {/* DecX AI Text */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <h2 style={{
          fontFamily: F,
          fontSize: size === 'small' ? '20px' : size === 'large' ? '32px' : '26px',
          fontWeight: 700,
          background: C.grad,
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          marginBottom: '8px'
        }}>
          DecX AI
        </h2>
        
        {text && (
          <p style={{
            fontFamily: "'Manrope', sans-serif",
            fontSize: '14px',
            color: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px'
          }}>
            <motion.span
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
              style={{
                display: 'inline-block',
                width: '14px',
                height: '14px',
                borderRadius: '50%',
                border: '2px solid transparent',
                borderTopColor: C.secondary,
                borderRightColor: C.secondary
              }}
            />
            {text}
          </p>
        )}
      </motion.div>
      
      {/* Did You Know Facts */}
      {showFacts && (
        <motion.div
          style={{
            maxWidth: '400px',
            marginTop: '16px',
            minHeight: '60px'
          }}
        >
          <AnimatePresence mode="wait">
            <motion.p
              key={currentFactIndex}
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -15 }}
              transition={{ duration: 0.4 }}
              style={{
                fontFamily: "'Manrope', sans-serif",
                fontSize: '13px',
                lineHeight: 1.6,
                color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
                fontStyle: 'italic',
                padding: '12px 20px',
                background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
                borderRadius: '12px',
                border: `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}`
              }}
            >
              {didYouKnowFacts[currentFactIndex]}
            </motion.p>
          </AnimatePresence>
        </motion.div>
      )}
      
      {/* Progress dots */}
      <div style={{
        display: 'flex',
        gap: '8px',
        marginTop: '8px'
      }}>
        {[0, 1, 2].map((i) => (
          <motion.div
            key={i}
            animate={{
              scale: [1, 1.3, 1],
              opacity: [0.3, 1, 0.3]
            }}
            transition={{
              duration: 1,
              repeat: Infinity,
              delay: i * 0.2
            }}
            style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              background: C.secondary
            }}
          />
        ))}
      </div>
    </motion.div>
  );
  
  if (fullScreen) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 9999,
          background: isDark 
            ? 'linear-gradient(135deg, #0a0a14, #1a1a2e)'
            : 'linear-gradient(135deg, #f8fafc, #e2e8f0)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        {content}
      </motion.div>
    );
  }
  
  return content;
};

// Intro/Splash screen loader with actual logos
export const IntroLoader = ({ onComplete, minDuration = 3000 }) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [progress, setProgress] = useState(0);
  const [completed, setCompleted] = useState(false);
  
  useEffect(() => {
    const startTime = Date.now();
    
    const progressInterval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const newProgress = Math.min((elapsed / minDuration) * 100, 100);
      setProgress(newProgress);
      
      if (newProgress >= 100 && !completed) {
        setCompleted(true);
        clearInterval(progressInterval);
        setTimeout(() => {
          onComplete?.();
        }, 500);
      }
    }, 50);
    
    return () => clearInterval(progressInterval);
  }, [minDuration, onComplete, completed]);
  
  // Determine logo paths based on theme
  const logoIconSrc = isDark ? '/logo-icon-dark.svg' : '/logo-icon-light.svg';
  const logoFullSrc = isDark ? '/logo-full-dark.svg' : '/logo-full-light.svg';
  
  return (
    <motion.div
      initial={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        background: isDark 
          ? 'linear-gradient(135deg, #0a0a14 0%, #1a1a2e 50%, #0f1629 100%)'
          : 'linear-gradient(135deg, #ffffff 0%, #f0f4f8 50%, #e8eef4 100%)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '24px'
      }}
    >
      {/* Animated Glow Background */}
      <motion.div
        style={{
          position: 'absolute',
          width: '400px',
          height: '400px',
          borderRadius: '50%',
          background: `radial-gradient(circle, ${C.secondary}25 0%, transparent 70%)`,
          filter: 'blur(80px)'
        }}
        animate={{
          scale: [1, 1.3, 1],
          opacity: [0.3, 0.5, 0.3]
        }}
        transition={{
          duration: 3,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      />
      
      {/* Logo Icon with Animation - LARGER */}
      <motion.div
        initial={{ scale: 0.5, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
        style={{ position: 'relative', zIndex: 1 }}
      >
        <motion.img
          src={logoIconSrc}
          alt="DecX AI"
          style={{
            width: '140px',
            height: '140px',
            objectFit: 'contain'
          }}
          animate={{
            scale: [1, 1.05, 1],
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
      </motion.div>
      
      {/* Full Logo Text - LARGER */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4, duration: 0.6 }}
        style={{ textAlign: 'center', position: 'relative', zIndex: 1 }}
      >
        <motion.img
          src={logoFullSrc}
          alt="DecX AI"
          style={{
            width: '280px',
            height: '80px',
            objectFit: 'contain'
          }}
        />
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
          style={{
            fontFamily: "'Manrope', sans-serif",
            fontSize: '13px',
            color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
            letterSpacing: '0.2em',
            textTransform: 'uppercase',
            marginTop: '16px'
          }}
        >
          Deck Intelligence Platform
        </motion.p>
      </motion.div>
      
      {/* Progress Bar - WIDER */}
      <motion.div
        initial={{ opacity: 0, width: 0 }}
        animate={{ opacity: 1, width: '280px' }}
        transition={{ delay: 0.6, duration: 0.4 }}
        style={{
          height: '4px',
          background: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
          borderRadius: '2px',
          overflow: 'hidden',
          position: 'relative',
          zIndex: 1,
          marginTop: '8px'
        }}
      >
        <motion.div
          style={{
            height: '100%',
            background: C.grad,
            borderRadius: '2px',
            width: `${progress}%`
          }}
        />
      </motion.div>
      
      {/* Loading Dots */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.8 }}
        style={{
          display: 'flex',
          gap: '10px',
          position: 'relative',
          zIndex: 1
        }}
      >
        {[0, 1, 2].map((i) => (
          <motion.div
            key={i}
            animate={{
              y: [0, -10, 0],
              opacity: [0.4, 1, 0.4]
            }}
            transition={{
              duration: 0.8,
              repeat: Infinity,
              delay: i * 0.15
            }}
            style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              background: C.secondary
            }}
          />
        ))}
      </motion.div>
    </motion.div>
  );
};

// Payment processing loader with extended duration
export const PaymentLoader = ({ 
  amount, 
  currency = 'INR',
  minDuration = 20000 // 20 seconds minimum 
}) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [currentFactIndex, setCurrentFactIndex] = useState(
    Math.floor(Math.random() * didYouKnowFacts.length)
  );
  const [progress, setProgress] = useState(0);
  
  // Rotate facts every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentFactIndex((prev) => (prev + 1) % didYouKnowFacts.length);
    }, 5000);
    
    return () => clearInterval(interval);
  }, []);
  
  // Simulated progress
  useEffect(() => {
    const startTime = Date.now();
    
    const progressInterval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      // Slow progress that reaches 90% and then slows down
      const newProgress = Math.min(
        90 * (1 - Math.exp(-elapsed / (minDuration * 0.4))),
        95
      );
      setProgress(newProgress);
    }, 100);
    
    return () => clearInterval(progressInterval);
  }, [minDuration]);
  
  const formatPrice = (amt) => {
    if (currency === 'USD') return `$${amt.toLocaleString('en-US')}`;
    return `₹${amt.toLocaleString('en-IN')}`;
  };
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '40px 20px',
        textAlign: 'center',
        minHeight: '400px'
      }}
    >
      {/* Logo */}
      <DecXLogo animate={true} size={72} />
      
      {/* Processing Text */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        style={{ marginTop: '28px', marginBottom: '24px' }}
      >
        <h3 style={{
          fontFamily: F,
          fontSize: '22px',
          fontWeight: 700,
          color: isDark ? '#fff' : '#1a1a2e',
          marginBottom: '8px'
        }}>
          Processing Payment
        </h3>
        <p style={{
          fontFamily: "'Manrope', sans-serif",
          fontSize: '16px',
          color: C.secondary,
          fontWeight: 600
        }}>
          {formatPrice(amount)}
        </p>
      </motion.div>
      
      {/* Progress Bar */}
      <div style={{
        width: '100%',
        maxWidth: '280px',
        marginBottom: '24px'
      }}>
        <div style={{
          height: '6px',
          background: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
          borderRadius: '3px',
          overflow: 'hidden'
        }}>
          <motion.div
            style={{
              height: '100%',
              background: C.grad,
              borderRadius: '3px'
            }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.3 }}
          />
        </div>
        <p style={{
          fontFamily: "'Manrope', sans-serif",
          fontSize: '12px',
          color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)',
          marginTop: '8px'
        }}>
          Please do not close this window...
        </p>
      </div>
      
      {/* Did You Know */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentFactIndex}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.4 }}
          style={{
            maxWidth: '360px',
            padding: '16px 20px',
            background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
            borderRadius: '12px',
            border: `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}`
          }}
        >
          <p style={{
            fontFamily: "'Manrope', sans-serif",
            fontSize: '13px',
            lineHeight: 1.6,
            color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
            fontStyle: 'italic'
          }}>
            {didYouKnowFacts[currentFactIndex]}
          </p>
        </motion.div>
      </AnimatePresence>
      
      {/* Security badges */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '16px',
        marginTop: '24px',
        color: isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
        fontSize: '11px'
      }}>
        <span>🔒 256-bit SSL</span>
        <span>•</span>
        <span>Secured by Razorpay</span>
      </div>
    </motion.div>
  );
};

export default BrandedLoader;
